import type { SeatCandidate } from './seat-identity';
import { bandDistance, type FitProfile, type HeightBand } from './fit';
import type { PriceDistribution } from './pricing';
import type { SeatPriority, ViewingPlan } from './viewing-journey';
import { layoutContextKey, officialLayoutAssessment } from './official-layout';
import type { SeatDuelCriterion } from './seat-duel';

export type EvidenceTolerance = 'exact_only' | 'nearby_ok' | 'unknown_ok';
export type LayoutFreshness = 'current' | 'changed' | 'unverified';
export type DecisionReadiness = 'ready_to_choose' | 'check_before_purchase';
export type FitEvidenceStatus = 'not_set' | 'unknown' | 'sample_only' | 'available' | 'official_recheck';
export type PriceEvidenceStatus = 'unknown' | 'sample_only' | 'available' | 'unresolved_grade';

export interface CandidateFitObservation {
  tier: 'exact' | 'nearby' | 'section';
  /** Response-scoped opaque reference. Equal values mean one contributor; null is excluded from people counts. */
  contributorRef: string | null;
  heightBand: HeightBand | null;
  knee: number | null;
  sound: number | null;
  access: number | null;
}

export interface CandidateEvidenceSnapshot {
  exact: number;
  nearby: number;
  section: number;
  otherConfiguration: number;
  observedDimensions: number;
  fitObservations?: CandidateFitObservation[];
  priceSampleCount?: number;
  priceDistribution?: PriceDistribution | null;
  priceGrade?: string | null;
  priceDistributionReason?: 'available' | 'no_recent_prices' | 'insufficient_sample' | 'seat_grade_unknown' | 'mixed_seat_grade';
}

export interface CandidateDecisionFact {
  candidateId: string;
  layoutFreshness: LayoutFreshness;
  evidenceTier: 'exact' | 'nearby' | 'section' | 'unknown';
  evidenceCounts: { exact: number; nearby: number; section: number };
  evidenceCount: number;
  priceStatus: 'within_budget' | 'over_budget' | 'unknown' | 'no_budget';
  priceEvidenceStatus: PriceEvidenceStatus;
  priceEvidenceStatement: string;
  fitStatus: FitEvidenceStatus;
  fitSampleCount: number;
  fitStatements: string[];
  readiness: DecisionReadiness;
  facts: string[];
  checks: string[];
}

export interface SeatDecisionRecord {
  id: string;
  clientOpId: string;
  eventTitle: string;
  candidates: SeatCandidate[];
  chosenCandidateId: string;
  fallbackCandidateIds: string[];
  unavailableCandidateIds: string[];
  evidenceTolerance: EvidenceTolerance;
  budgetPerPerson: number | null;
  seatPriorities: SeatPriority[];
  connectedSeatsRequired: boolean;
  headCount: number;
  note: string;
  savedAt: string;
  lastCheckedAt: string;
  comparisonCriterion?: SeatDuelCriterion;
}

const tolerances = new Set<EvidenceTolerance>(['exact_only', 'nearby_ok', 'unknown_ok']);
const seatPriorities = new Set<SeatPriority>(['full_view', 'close_view', 'legroom', 'aisle', 'low_stairs', 'lower_sensory']);
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

/** A new decision must not evict a saved decision without the user's removal. */
export function retainSeatDecision(records: SeatDecisionRecord[], decision: SeatDecisionRecord): SeatDecisionRecord[] | null {
  const normalized = normalizeSeatDecision(decision);
  if (!normalized || records.length >= 24 && !records.some((entry) => entry.id === normalized.id)) return null;
  return [normalized, ...records.filter((entry) => entry.id !== normalized.id)];
}

export function candidateSeatKey(candidate: SeatCandidate): string {
  return [candidate.venueId, candidate.layoutVersionId, candidate.sectionCode, candidate.rowLabel, candidate.seatNumber].join(':');
}

export function buildCandidateDecisionFacts(
  candidates: SeatCandidate[],
  plan: Pick<ViewingPlan, 'budgetPerPerson' | 'headCount' | 'connectedSeatsRequired'>,
  evidenceByCandidate: Record<string, CandidateEvidenceSnapshot | undefined>,
  activeLayoutByVenue: Record<string, string | null | undefined>,
  evidenceTolerance: EvidenceTolerance,
  fitProfile?: FitProfile,
): CandidateDecisionFact[] {
  return candidates.map((candidate) => {
    const evidence = evidenceByCandidate[candidate.candidateId] ?? emptyEvidence();
    const contextKey = layoutContextKey(candidate);
    const activeLayout = Object.hasOwn(activeLayoutByVenue, contextKey)
      ? activeLayoutByVenue[contextKey]
      : activeLayoutByVenue[candidate.venueId];
    const official = officialLayoutAssessment(candidate);
    const layoutFreshness: LayoutFreshness = candidate.layoutVersionId.toLowerCase().includes('unconfirmed')
      ? 'unverified'
      : activeLayout === undefined || activeLayout === null
      ? 'unverified'
      : activeLayout !== candidate.layoutVersionId ? 'changed'
      : official.layout ? 'unverified' : 'current';
    const evidenceTier = evidence.exact > 0 ? 'exact'
      : evidence.nearby > 0 ? 'nearby'
        : evidence.section > 0 ? 'section' : 'unknown';
    const evidenceCounts = { exact: evidence.exact, nearby: evidence.nearby, section: evidence.section };
    const evidenceCount = evidenceTier === 'unknown' ? 0 : evidenceCounts[evidenceTier];
    const priceStatus = plan.budgetPerPerson === null
      ? candidate.expectedPrice === null ? 'unknown' : 'no_budget'
      : candidate.expectedPrice === null ? 'unknown'
        : candidate.expectedPrice <= plan.budgetPerPerson ? 'within_budget' : 'over_budget';
    const facts: string[] = [];
    const checks: string[] = [];
    const priceEvidence = buildPriceEvidence(evidence);
    const fitEvidence = buildFitEvidence(fitProfile, evidence.fitObservations ?? [], evidenceTolerance);

    if (layoutFreshness === 'current') facts.push('현재 공식 배치와 같은 버전이에요.');
    if (layoutFreshness === 'changed') checks.push('저장한 후보 뒤로 공식 배치가 바뀌었어요. 좌석을 다시 담아야 해요.');
    if (layoutFreshness === 'unverified') checks.push('현재 공식 배치 버전을 확인하지 못했어요.');

    if (evidenceTier === 'exact') facts.push(`같은 좌석 근거 ${evidence.exact}건이 있어요.`);
    if (evidenceTier === 'nearby') facts.push(`가까운 좌석 근거 ${evidence.nearby}건이 있어요.`);
    if (evidenceTier === 'section') facts.push(`같은 구역 근거 ${evidence.section}건만 있어요.`);
    if (evidenceTier === 'unknown') checks.push('이 좌석에 적용할 관람 근거가 아직 없어요.');
    if (evidence.otherConfiguration > 0) checks.push(`다른 무대 구성 기록 ${evidence.otherConfiguration}건은 이번 판단에서 뺐어요.`);

    if (priceStatus === 'within_budget') facts.push('입력한 1인 예산 안이에요.');
    if (priceStatus === 'over_budget') checks.push(`입력한 1인 예산보다 ${(candidate.expectedPrice! - plan.budgetPerPerson!).toLocaleString('ko-KR')}원 높아요.`);
    if (priceStatus === 'unknown') checks.push('예상 결제 금액을 확인하지 못했어요.');
    if (priceStatus === 'no_budget') facts.push('예상 결제 금액이 입력돼 있어요.');
    if (priceEvidence.status === 'available') facts.push(priceEvidence.statement);
    else checks.push(priceEvidence.statement);

    facts.push(...fitEvidence.facts);
    checks.push(...fitEvidence.checks);

    if (plan.headCount > 1 && plan.connectedSeatsRequired) checks.push(`붙어 앉을 ${plan.headCount}석의 실제 재고는 공식 예매처에서 확인해야 해요.`);
    if (!meetsEvidenceTolerance(evidenceTier, evidenceTolerance)) checks.push(evidenceTolerance === 'exact_only'
      ? '정확히 같은 좌석 근거가 생기기 전에는 보류하는 기준이에요.'
      : '가까운 좌석 이상의 근거가 생기기 전에는 보류하는 기준이에요.');

    const blocking = layoutFreshness !== 'current'
      || priceStatus === 'over_budget'
      || !meetsEvidenceTolerance(evidenceTier, evidenceTolerance)
      || fitEvidence.blocking;

    return {
      candidateId: candidate.candidateId,
      layoutFreshness,
      evidenceTier,
      evidenceCounts,
      evidenceCount,
      priceStatus,
      priceEvidenceStatus: priceEvidence.status,
      priceEvidenceStatement: priceEvidence.statement,
      fitStatus: fitEvidence.status,
      fitSampleCount: fitEvidence.sampleCount,
      fitStatements: [...fitEvidence.facts, ...fitEvidence.checks],
      readiness: blocking ? 'check_before_purchase' : 'ready_to_choose',
      facts,
      checks: [...new Set(checks)],
    };
  });
}

/** Questions concern facts a venue or booking provider can check, never a fit guarantee. */
export function buildBookingQuestions(candidate: SeatCandidate, fact: CandidateDecisionFact, plan: Pick<ViewingPlan, 'eventTitle' | 'startsAt' | 'headCount' | 'connectedSeatsRequired' | 'seatPriorities'>): { context: string; questions: string[]; text: string } {
  const context = [plan.eventTitle.trim() || '관람할 공연·영화', plan.startsAt || '관람 날짜·회차를 적어 주세요', `${candidate.venueName} ${candidate.spaceName}`, `${candidate.sectionCode} ${candidate.rowLabel}열 ${candidate.seatNumber}번`].join(' · ');
  const questions: string[] = [];
  if (fact.layoutFreshness !== 'current') questions.push('이 회차의 공식 좌석 배치에서 해당 좌석 위치와 무대 구성이 맞나요? 변경된 좌석도나 공지가 있나요?');
  if (fact.evidenceTier !== 'exact' || fact.layoutFreshness !== 'current') questions.push('이 좌석에 난간·기둥·공연 장비로 인한 시야 제한이 있나요? 예매 시 안내되는 시야 제한 조건을 알려 주세요.');
  if (fact.priceStatus === 'unknown' || fact.priceStatus === 'over_budget' || fact.priceEvidenceStatus !== 'available') questions.push('이 회차·좌석 등급의 수수료 포함 최종 결제 금액과 할인 적용 조건은 무엇인가요?');
  if (plan.connectedSeatsRequired && plan.headCount > 1) questions.push(`이 좌석을 포함해 ${plan.headCount}명이 붙어 앉는 좌석이 현재 남아 있나요? 통로로 떨어지는 자리는 아닌가요?`);
  if (fact.fitStatus === 'official_recheck' || plan.seatPriorities.includes('low_stairs')) questions.push('입구에서 이 좌석까지 계단 없는 이동 경로와 이용 가능한 이동 지원이 있나요? 미리 신청해야 하나요?');
  if (plan.seatPriorities.includes('legroom') && fact.fitStatus !== 'available') questions.push('이 좌석 앞의 고정 구조물과 좌석 간격을 확인할 수 있나요?');
  if (plan.seatPriorities.includes('lower_sensory') && fact.fitStatus !== 'available') questions.push('이 회차의 스피커 위치와 강한 빛·큰 소리 안내를 확인할 수 있나요?');
  if (!questions.length) questions.push('이 회차·좌석의 최신 시야 제한 공지와 예매 조건에 변경이 있나요?');
  return { context, questions, text: `${context}\n\n${questions.map((question, index) => `${index + 1}. ${question}`).join('\n')}` };
}

function buildPriceEvidence(evidence: CandidateEvidenceSnapshot): { status: PriceEvidenceStatus; statement: string } {
  const count = Math.max(0, evidence.priceSampleCount ?? evidence.priceDistribution?.n ?? 0);
  const distribution = evidence.priceDistribution;
  if (distribution && distribution.n >= 5 && evidence.priceDistributionReason === 'available') {
    const grade = evidence.priceGrade ? ` · ${evidence.priceGrade}등급` : '';
    return {
      status: 'available',
      statement: `같은 배치${grade} 실제 낸 돈 중앙값 ${distribution.median.toLocaleString('ko-KR')}원 (${distribution.q1.toLocaleString('ko-KR')}~${distribution.q3.toLocaleString('ko-KR')}원, ${distribution.n}명, 최근 24개월)이에요.`,
    };
  }
  if (evidence.priceDistributionReason === 'seat_grade_unknown' || evidence.priceDistributionReason === 'mixed_seat_grade') {
    return { status: 'unresolved_grade', statement: `최근 24개월 실제 낸 돈 기록 ${count}건은 좌석 등급 일치를 확인하지 못해 분포를 만들지 않아요.` };
  }
  if (count > 0) return { status: 'sample_only', statement: `최근 24개월 실제 낸 돈 기록이 아직 ${count}건이에요. 5건 전에는 금액 분포를 보여 주지 않아요.` };
  return { status: 'unknown', statement: '최근 24개월 실제 낸 돈 기록은 아직 모름이에요.' };
}

export function buildFitEvidence(profile: FitProfile | undefined, observations: CandidateFitObservation[], tolerance: EvidenceTolerance): {
  status: FitEvidenceStatus;
  sampleCount: number;
  facts: string[];
  checks: string[];
  blocking: boolean;
} {
  if (!profile || !hasFitPreference(profile)) {
    return { status: 'not_set', sampleCount: 0, facts: [], checks: ['내 몸·감각 조건을 넣지 않았어요. 입력하지 않은 조건은 추측하지 않아요.'], blocking: false };
  }

  const facts: string[] = [];
  const checks: string[] = [];
  let blocking = false;
  let status: FitEvidenceStatus = 'unknown';
  const allowed = observations.filter((row) => meetsEvidenceTolerance(row.tier, tolerance));
  const byContributor = new Map<string, CandidateFitObservation>();
  for (const row of allowed) {
    const contributorRef = row.contributorRef?.trim();
    if (!contributorRef) continue;
    const current = byContributor.get(contributorRef);
    if (!current || evidenceTierRank(row.tier) < evidenceTierRank(current.tier)) byContributor.set(contributorRef, row);
  }
  const distinctContributors = [...byContributor.values()];
  const cohort = profile.heightBand === null ? [] : distinctContributors.filter((row) => {
    const distance = bandDistance(profile.heightBand, row.heightBand);
    return distance !== null && distance <= 1;
  });
  const unlinkedFitRows = profile.heightBand === null ? 0 : allowed.filter((row) => {
    if (row.contributorRef?.trim()) return false;
    const distance = bandDistance(profile.heightBand, row.heightBand);
    return distance !== null && distance <= 1;
  }).length;

  if (unlinkedFitRows > 0) checks.push(`작성자를 구분할 수 없는 체형 기록 ${unlinkedFitRows}건은 인원 표본에서 뺐어요.`);

  if (profile.heightBand === null) {
    checks.push('키 범위가 없어 비슷한 체형 표본을 묶지 않아요.');
    if (importantFitPreference(profile)) blocking = true;
  } else if (cohort.length === 0) {
    checks.push('내 키와 비슷한 관람 기록은 아직 모름이에요.');
    if (importantFitPreference(profile)) blocking = true;
  } else if (cohort.length < 3) {
    status = 'sample_only';
    checks.push(`내 키와 비슷한 기록 ${cohort.length}명이 있어요. 표본이 적어 불편 비율은 만들지 않아요.`);
    if (importantFitPreference(profile)) blocking = true;
  } else {
    status = 'available';
    facts.push(`내 키와 비슷한 기록 ${cohort.length}명을 같은 근거 범위에서 확인했어요.`);
    if ((profile.knee ?? 0) >= 2) {
      const known = cohort.filter((row) => row.knee !== null);
      if (known.length < 3) {
        checks.push(`무릎 불편을 확인한 기록은 ${known.length}명이에요. 모르는 값은 불편 없음으로 계산하지 않고, 3명 전에는 비율을 만들지 않아요.`);
        status = known.length ? 'sample_only' : 'unknown';
        blocking = true;
      } else {
        const uncomfortable = known.filter((row) => row.knee !== null && row.knee >= 2).length;
        facts.push(`${known.length === cohort.length ? '그중' : `무릎 기록 ${known.length}명 중`} ${uncomfortable}명이 무릎 불편을 2단계 이상으로 남겼어요.`);
      }
    }
    if ((profile.soundLight ?? 0) >= 2) {
      const known = cohort.filter((row) => row.sound !== null);
      if (known.length < 3) {
        checks.push(`소리 불편을 확인한 기록은 ${known.length}명이에요. 모르는 값은 불편 없음으로 계산하지 않고, 빛은 별도 근거가 없어 포함하지 않아요.`);
        if (status !== 'unknown') status = known.length ? 'sample_only' : 'unknown';
        blocking = true;
      } else {
        const uncomfortable = known.filter((row) => row.sound !== null && row.sound >= 2).length;
        facts.push(`${known.length === cohort.length ? '그중' : `소리 기록 ${known.length}명 중`} ${uncomfortable}명이 소리 불편을 2단계 이상으로 남겼어요. 빛은 별도 근거가 없어 포함하지 않아요.`);
      }
    }
  }

  if ((profile.neckBack ?? 0) >= 2) {
    checks.push('목·허리 각도는 직접 수집한 근거가 없어 모름이에요.');
    blocking = true;
  }
  if (profile.mobilityNeed === true) {
    checks.push('출입 점수는 접근 가능 보장이 아니에요. 공식 장소의 이동 지원 동선을 다시 확인해 주세요.');
    blocking = true;
    status = 'official_recheck';
  }
  return { status, sampleCount: cohort.length, facts, checks, blocking };
}

function hasFitPreference(profile: FitProfile): boolean {
  return profile.heightBand !== null || profile.knee !== null || profile.neckBack !== null || profile.soundLight !== null || profile.mobilityNeed !== null;
}

function importantFitPreference(profile: FitProfile): boolean {
  return (profile.knee ?? 0) >= 2 || (profile.neckBack ?? 0) >= 2 || (profile.soundLight ?? 0) >= 2 || profile.mobilityNeed === true;
}

function evidenceTierRank(tier: CandidateFitObservation['tier']): number {
  return tier === 'exact' ? 0 : tier === 'nearby' ? 1 : 2;
}

export function createSeatDecision(
  candidates: SeatCandidate[],
  plan: Pick<ViewingPlan, 'eventTitle' | 'budgetPerPerson' | 'seatPriorities' | 'connectedSeatsRequired' | 'headCount'>,
  chosenCandidateId: string,
  fallbackCandidateIds: string[],
  unavailableCandidateIds: string[],
  evidenceTolerance: EvidenceTolerance,
  note: string,
  savedAt = new Date().toISOString(),
): SeatDecisionRecord {
  const ids = new Set(candidates.map((candidate) => candidate.candidateId));
  if (!ids.has(chosenCandidateId)) throw new Error('1순위 좌석을 후보에서 다시 골라 주세요.');
  const fallbacks = fallbackCandidateIds.filter((id, index) => ids.has(id) && id !== chosenCandidateId && fallbackCandidateIds.indexOf(id) === index);
  return {
    id: `${Date.parse(savedAt) || Date.now()}-${chosenCandidateId}`,
    clientOpId: crypto.randomUUID(),
    eventTitle: plan.eventTitle.trim().slice(0, 120),
    candidates: candidates.slice(0, 4),
    chosenCandidateId,
    fallbackCandidateIds: fallbacks,
    unavailableCandidateIds: [...new Set(unavailableCandidateIds.filter((id) => ids.has(id)))],
    evidenceTolerance,
    budgetPerPerson: plan.budgetPerPerson,
    seatPriorities: plan.seatPriorities.slice(0, 3),
    connectedSeatsRequired: plan.connectedSeatsRequired,
    headCount: plan.headCount,
    note: note.trim().slice(0, 500),
    savedAt,
    lastCheckedAt: savedAt,
  };
}

export function normalizeSeatDecision(value: unknown): SeatDecisionRecord | null {
  if (!value || typeof value !== 'object') return null;
  const row = value as Partial<SeatDecisionRecord>;
  if (row.comparisonCriterion !== undefined && !['aisle', 'center', 'price'].includes(row.comparisonCriterion)) return null;
  const candidates = Array.isArray(row.candidates) ? row.candidates.filter(isSeatCandidate).slice(0, 4) : [];
  const ids = new Set(candidates.map((candidate) => candidate.candidateId));
  if (typeof row.chosenCandidateId !== 'string' || !ids.has(row.chosenCandidateId)) return null;
  const fallbackCandidateIds = Array.isArray(row.fallbackCandidateIds)
    ? row.fallbackCandidateIds.filter((id): id is string => typeof id === 'string' && ids.has(id) && id !== row.chosenCandidateId).slice(0, 3)
    : [];
  const unavailableCandidateIds = Array.isArray(row.unavailableCandidateIds)
    ? row.unavailableCandidateIds.filter((id): id is string => typeof id === 'string' && ids.has(id)).slice(0, 4)
    : [];
  return {
    id: typeof row.id === 'string' && row.id.trim() ? row.id.slice(0, 240) : `${Date.now()}-${row.chosenCandidateId}`,
    clientOpId: typeof row.clientOpId === 'string' && uuid.test(row.clientOpId) ? row.clientOpId : crypto.randomUUID(),
    eventTitle: typeof row.eventTitle === 'string' ? row.eventTitle.trim().slice(0, 120) : '',
    candidates,
    chosenCandidateId: row.chosenCandidateId,
    fallbackCandidateIds: [...new Set(fallbackCandidateIds)],
    unavailableCandidateIds: [...new Set(unavailableCandidateIds)],
    evidenceTolerance: tolerances.has(row.evidenceTolerance as EvidenceTolerance) ? row.evidenceTolerance as EvidenceTolerance : 'nearby_ok',
    budgetPerPerson: typeof row.budgetPerPerson === 'number' && Number.isInteger(row.budgetPerPerson) && row.budgetPerPerson >= 0 && row.budgetPerPerson <= 10_000_000 ? row.budgetPerPerson : null,
    seatPriorities: Array.isArray(row.seatPriorities) ? row.seatPriorities.filter((value): value is SeatPriority => seatPriorities.has(value as SeatPriority)).slice(0, 3) : [],
    connectedSeatsRequired: typeof row.connectedSeatsRequired === 'boolean' ? row.connectedSeatsRequired : true,
    headCount: typeof row.headCount === 'number' && Number.isInteger(row.headCount) && row.headCount >= 1 && row.headCount <= 12 ? row.headCount : 1,
    note: typeof row.note === 'string' ? row.note.slice(0, 500) : '',
    savedAt: validDate(row.savedAt) ? row.savedAt : new Date(0).toISOString(),
    lastCheckedAt: validDate(row.lastCheckedAt) ? row.lastCheckedAt : validDate(row.savedAt) ? row.savedAt : new Date(0).toISOString(),
    ...(row.comparisonCriterion ? { comparisonCriterion: row.comparisonCriterion } : {}),
  };
}

export function promoteFallback(record: SeatDecisionRecord, at = new Date().toISOString()): SeatDecisionRecord {
  const next = record.fallbackCandidateIds.find((id) => !record.unavailableCandidateIds.includes(id));
  if (!next) throw new Error('남은 대안 좌석이 없어요. 후보를 다시 담아 주세요.');
  const unavailableCandidateIds = [...new Set([...record.unavailableCandidateIds, record.chosenCandidateId])];
  return {
    ...record,
    chosenCandidateId: next,
    fallbackCandidateIds: record.fallbackCandidateIds.filter((id) => id !== next),
    unavailableCandidateIds,
    lastCheckedAt: at,
  };
}

export function buildDecisionShareText(record: SeatDecisionRecord, includePrice = false): string {
  const label = (id: string) => {
    const candidate = record.candidates.find((item) => item.candidateId === id);
    if (!candidate) return null;
    const price = includePrice && candidate.expectedPrice !== null ? ` · 예상 ${candidate.expectedPrice.toLocaleString('ko-KR')}원` : '';
    return `${candidate.venueName} ${candidate.spaceName} · ${candidate.sectionCode} ${candidate.rowLabel}열 ${candidate.seatNumber}번${price}`;
  };
  const primary = label(record.chosenCandidateId);
  const alternatives = record.fallbackCandidateIds.map(label).filter(Boolean);
  return [
    record.eventTitle || '앉아봄 좌석 결정',
    primary ? `1순위: ${primary}` : null,
    ...alternatives.map((value, index) => `대안 ${index + 1}: ${value}`),
    record.note ? `함께 확인할 점: ${record.note}` : null,
    '공식 예매처에서 현재 배치·연석·가격을 다시 확인해 주세요.',
  ].filter(Boolean).join('\n');
}

function emptyEvidence(): CandidateEvidenceSnapshot {
  return { exact: 0, nearby: 0, section: 0, otherConfiguration: 0, observedDimensions: 0 };
}

function meetsEvidenceTolerance(tier: CandidateDecisionFact['evidenceTier'], tolerance: EvidenceTolerance): boolean {
  if (tolerance === 'unknown_ok') return true;
  if (tolerance === 'nearby_ok') return tier === 'exact' || tier === 'nearby';
  return tier === 'exact';
}

export function isSeatCandidate(value: unknown): value is SeatCandidate {
  if (!value || typeof value !== 'object') return false;
  const row = value as Partial<SeatCandidate>;
  return (row.categoryId === 'performance' || row.categoryId === 'cinema')
    && ['candidateId', 'venueId', 'venueName', 'spaceId', 'spaceName', 'layoutVersionId', 'sectionCode', 'rowLabel'].every((key) => typeof row[key as keyof SeatCandidate] === 'string')
    && Number.isInteger(row.seatNumber) && Number(row.seatNumber) > 0
    && (row.expectedPrice === null || (typeof row.expectedPrice === 'number' && Number.isFinite(row.expectedPrice) && row.expectedPrice >= 0));
}

function validDate(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}
